import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Activity,
  AlertOctagon,
  Bell,
  BellRing,
  BookOpen,
  Brain,
  Building2,
  Cable,
  CalendarDays,
  Check,
  ClipboardCheck,
  Folder,
  Gavel,
  Globe,
  Home,
  Lightbulb,
  LineChart,
  ListChecks,
  type LucideIcon,
  Moon,
  Plane,
  Plug,
  Plus,
  Settings,
  Shield,
  ShieldCheck,
  Target,
  User,
  Users,
  Workflow,
} from 'lucide-react';
import { ConnectorStatus, TodoStatus, type OrgSummary } from '@leanopx/shared';
import { useActiveOrg, useAuth, useIsOrgAdmin } from '@/components/providers/auth-provider';
import { useCommandPaletteStore } from '@/stores/command-palette-store';
import { useUiStore } from '@/stores/ui-store';
import { BrandMark } from './brand-mark';
import { api, type PersonalizationProfile } from '@/lib/api';
import { warnUnlessAbort } from '@/lib/logger';
import { t, tFormat } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { initialsFor } from '@/lib/initials';
import { toast } from '@/lib/toast';

/**
 * SidebarNav — two-tier sidebar (icon rail + contextual panel), per the
 * v6 designer mock (`Sidebar` in the LeanOpx Platform handoff bundle).
 *
 *   ┌──┬───────────┐
 *   │ ●│  HOME     │
 *   │  │  Briefing │
 *   │  │  Briefs   │
 *   │  │  …        │
 *   ├──┤           │
 *   │ ●│           │   (icon rail)   (contextual panel)
 *   │ ●│           │
 *   ├──┤           │
 *   │ S│           │
 *   │ L│           │   (settings + workspace pill at bottom)
 *   └──┴───────────┘
 *
 * Six logical sections:
 *   1. home       — Briefing, Briefs, Overnight retro
 *   2. operate    — Scorecard, Rocks, Issues, Approvals, Flight plan, Playbook
 *   3. inbox      — Signals, Alerts, Todos
 *   4. knowledge  — Dossiers, People, Insights, Outside view, Memory, …
 *   5. trust      — Agent log, Agent plan, Sources, Audit log
 *   6. settings   — Personalization, Company profile, Integrations, Rules, Permissions
 *
 * Active-section derivation: the section that contains the currently
 * routed surface gets highlighted in the rail; its items render in the
 * contextual panel. Clicking a rail icon navigates to the section's
 * first non-admin item (so the section "becomes" active via the
 * normal route-change flow).
 *
 * Per-row admin gating: rows tagged `adminOnly: true` in the section
 * arrays below — People (Knowledge); Audit log (Trust); Integrations,
 * Rules, Permissions, Agent settings, and Settings (Settings section)
 * — are filtered out of `visibleSections` for non-admins via
 * `useIsOrgAdmin()`. `/profile/company` is NOT admin-only in the
 * sidebar (the route is `requireMembership`-gated in router.tsx and
 * the page renders told rows read-only for non-admins), and `/profile`
 * has no route gate at all — those two member-readable rows keep the
 * Settings rail-button visible for every member.
 *
 * Pin/collapse (⌘\): when `sidePinned === false` the parent shell adds
 * `.is-side-collapsed`, which hides the contextual panel — the icon
 * rail stays visible. Hover-expand (CSS-only) re-reveals the panel.
 */

type SectionId = 'home' | 'operate' | 'inbox' | 'knowledge' | 'trust' | 'settings';

interface NavRow {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: { value: string; tone?: 'amber' | 'rose' };
  /** When true, only renders for `useIsOrgAdmin() === true` users. */
  adminOnly?: boolean;
}

interface NavSection {
  id: SectionId;
  /** Icon for the icon rail. */
  icon: LucideIcon;
  /** Tooltip + contextual-panel header. */
  label: string;
  /** Rows that appear in this section's contextual panel. */
  rows: NavRow[];
}

// Home section — the morning flow. Briefing on top (the canonical
// landing page), then the brief archive, then the overnight retro.
const HOME_ROWS: NavRow[] = [
  { to: '/today', label: t('sidebar.label.briefing'), icon: Home },
  { to: '/briefs', label: t('nav.briefs'), icon: BookOpen },
  { to: '/briefing/overnight', label: t('sidebar.overnightRetro'), icon: Moon },
];

// Operate section — the weekly EOS-cadence surfaces + the operator's
// approval queue. /approvals + /approvals/flight-plan live here in the
// new layout because both are "what does the operator need to act on"
// — distinct from /signals + /alerts which are read-only feeds.
const OPERATE_ROWS: NavRow[] = [
  { to: '/scorecard', label: t('sidebar.label.scorecard'), icon: LineChart },
  { to: '/rocks', label: t('sidebar.label.rocks'), icon: Target },
  { to: '/issues', label: t('sidebar.label.issues'), icon: AlertOctagon },
  { to: '/approvals', label: t('sidebar.approvals'), icon: ClipboardCheck },
  { to: '/approvals/flight-plan', label: t('sidebar.flightPlan'), icon: Plane },
  { to: '/playbook', label: t('sidebar.label.playbook'), icon: BookOpen },
];

// Inbox section — proactive feeds the agent surfaces (read-only).
const INBOX_ROWS: NavRow[] = [
  { to: '/signals', label: t('sidebar.label.signals'), icon: Bell },
  { to: '/alerts', label: t('sidebar.label.alerts'), icon: BellRing },
  { to: '/todos', label: t('sidebar.label.todos'), icon: ListChecks },
];

// Knowledge section — dossier surfaces + research + decisions log.
// /admin/people is admin-only at the route layer.
const KNOWLEDGE_ROWS: NavRow[] = [
  { to: '/dossiers', label: t('sidebar.label.dossiers'), icon: Folder },
  { to: '/admin/people', label: t('sidebar.label.people'), icon: Users, adminOnly: true },
  { to: '/insights', label: t('sidebar.label.insights'), icon: Lightbulb },
  { to: '/signals/external', label: t('sidebar.label.outsideView'), icon: Globe },
  { to: '/memory', label: t('sidebar.memory'), icon: Brain },
  { to: '/memory/near-misses', label: t('sidebar.nearMisses'), icon: ShieldCheck },
  { to: '/decisions', label: t('sidebar.decisions'), icon: Gavel },
  { to: '/notes/timeline', label: t('sidebar.notesTimeline'), icon: CalendarDays },
];

// Trust section — provenance + integrations. Per the v6 designer mock,
// the Trust panel reads "Sources / Agent log / Agent plan / Audit log".
// Sources, Agent log, and Agent plan are member-readable (no route
// gate / `requireMembership` in router.tsx); Audit log is the only
// admin-gated row in this section and is filtered out for non-admins
// via the `adminOnly` flag.
const TRUST_ROWS: NavRow[] = [
  { to: '/sources', label: t('sidebar.label.sources'), icon: Cable },
  { to: '/agent', label: t('sidebar.label.agentLog'), icon: Activity },
  { to: '/agent/flight-plan', label: t('sidebar.agentFlightPlan'), icon: Plane },
  { to: '/admin/audit', label: t('sidebar.label.audit'), icon: Shield, adminOnly: true },
];

// Settings section. `/profile` (personal profile, no route gate) and
// `/profile/company` (`requireMembership` in router.tsx — every org
// member can read it; admin-only edits enforced inside the page) are
// member-readable rows that keep the section non-empty for non-admins.
// The remaining rows — Integrations, Rules, Permissions, Agent
// settings, Settings — are `requireOrgAdmin`-gated at the route layer
// and carry `adminOnly: true` here so non-admins don't see dead links.
const SETTINGS_ROWS: NavRow[] = [
  { to: '/profile', label: t('sidebar.personalization'), icon: User },
  { to: '/profile/company', label: t('sidebar.companyProfile'), icon: Building2 },
  { to: '/admin/integrations', label: t('sidebar.integrations'), icon: Plug, adminOnly: true },
  { to: '/rules', label: t('sidebar.rules'), icon: Workflow, adminOnly: true },
  { to: '/permissions', label: t('sidebar.permissions'), icon: ShieldCheck, adminOnly: true },
  { to: '/settings/agent', label: t('sidebar.agentSettings'), icon: Settings, adminOnly: true },
  { to: '/admin/settings', label: t('sidebar.label.settings'), icon: Settings, adminOnly: true },
];

const SECTIONS: NavSection[] = [
  { id: 'home', icon: Home, label: t('sidebar.section.home'), rows: HOME_ROWS },
  { id: 'operate', icon: Target, label: t('sidebar.section.operate'), rows: OPERATE_ROWS },
  { id: 'inbox', icon: Bell, label: t('sidebar.section.inbox'), rows: INBOX_ROWS },
  { id: 'knowledge', icon: Folder, label: t('sidebar.section.knowledge'), rows: KNOWLEDGE_ROWS },
  { id: 'trust', icon: Plug, label: t('sidebar.section.trust'), rows: TRUST_ROWS },
  { id: 'settings', icon: Settings, label: t('sidebar.section.settings'), rows: SETTINGS_ROWS },
];

const SURFACE_BADGE_TONES: Record<string, 'amber' | 'rose'> = {
  '/insights': 'amber',
  '/signals': 'amber',
  '/alerts': 'rose',
  '/approvals': 'amber',
  '/todos': 'amber',
};

interface NavCounts {
  signals: number;
  alerts: number;
  approvals: number;
  insights: number;
  todos: number;
  sources: number;
}

/**
 * Live nav-badge counts. Fetches the relevant surface lists in parallel
 * once per org and counts client-side. Each call fails soft (badge stays
 * hidden on error) so a single 5xx never strands the whole sidebar.
 */
function useNavCounts(orgId: string): NavCounts | null {
  const [counts, setCounts] = useState<NavCounts | null>(null);
  useEffect(() => {
    let cancelled = false;
    setCounts(null);
    Promise.all([
      api
        .listSignals(orgId)
        .then((r) => r.signals.length)
        .catch((err) => {
          warnUnlessAbort('sidebar.signals count failed', err);
          return 0;
        }),
      api
        .listAlerts(orgId)
        .then((r) => r.signals.length)
        .catch((err) => {
          warnUnlessAbort('sidebar.alerts count failed', err);
          return 0;
        }),
      api
        .listApprovals(orgId)
        .then((r) => r.drafts.length)
        .catch((err) => {
          warnUnlessAbort('sidebar.approvals count failed', err);
          return 0;
        }),
      api
        .listInsights(orgId)
        .then((r) => r.insights.length)
        .catch((err) => {
          warnUnlessAbort('sidebar.insights count failed', err);
          return 0;
        }),
      api
        .listTodos(orgId)
        .then((r) => r.todos.filter((todo) => todo.status !== TodoStatus.enum.done).length)
        .catch((err) => {
          warnUnlessAbort('sidebar.todos count failed', err);
          return 0;
        }),
      api
        .listConnectors(orgId)
        .then((r) => r.connectors.filter((c) => c.status === ConnectorStatus.enum.active).length)
        .catch((err) => {
          warnUnlessAbort('sidebar.sources count failed', err);
          return 0;
        }),
    ]).then(([signals, alerts, approvals, insights, todos, sources]) => {
      if (cancelled) return;
      setCounts({ signals, alerts, approvals, insights, todos, sources });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);
  return counts;
}

/**
 * Route-active state → which section "owns" the currently-rendered
 * page. We match by longest-prefix on row.to (e.g. /memory/near-misses
 * resolves before /memory in the Knowledge section). When nothing
 * matches, default to the home section so the sidebar doesn't render
 * empty on unknown routes.
 */
function activeSectionFor(pathname: string): SectionId {
  let best: { sectionId: SectionId; len: number } | null = null;
  for (const section of SECTIONS) {
    for (const row of section.rows) {
      const matches =
        pathname === row.to ||
        (row.to !== '/' && pathname.startsWith(`${row.to}/`)) ||
        (row.to === '/today' && pathname === '/');
      if (!matches) continue;
      if (!best || row.to.length > best.len) {
        best = { sectionId: section.id, len: row.to.length };
      }
    }
  }
  // Settings subpages not explicitly listed in SETTINGS_ROWS still
  // belong to the Settings section (e.g. /settings/cadence,
  // /settings/sources, /settings/org/*). Without this fallback they
  // would fall through to 'home' and show the wrong contextual panel
  // and rail highlight.
  if (best === null && pathname.startsWith('/settings/')) {
    return 'settings';
  }
  return best?.sectionId ?? 'home';
}

export function SidebarNav() {
  const { user, activeOrg, memberships, switchOrg } = useAuth();
  const isOrgAdmin = useIsOrgAdmin();
  const { id: orgId } = useActiveOrg();
  const sidePinned = useUiStore((s) => s.sidePinned);
  const toggleSidePin = useUiStore((s) => s.toggleSidePin);
  const openPalette = useCommandPaletteStore((s) => s.setOpen);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Personalization profile drives nav reorder within the Home section
  // (the v6 personalization scope only re-orders NAV_PRIMARY paths).
  const [profile, setProfile] = useState<PersonalizationProfile | null>(null);
  useEffect(() => {
    let cancelled = false;
    setProfile(null);
    api
      .getPersonalization(orgId)
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((err) => {
        if (!cancelled) warnUnlessAbort('sidebar.personalization load failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const navCounts = useNavCounts(orgId);

  // Active section is route-derived; user-controlled rail clicks
  // navigate to that section's first item, which then drives this
  // computation on the next render.
  const activeSection = useMemo(() => activeSectionFor(pathname), [pathname]);

  // Compose the visible sections — admin gate + badge counts + Home
  // personalization order — into a single derived array. Re-runs only
  // when one of these inputs changes.
  const visibleSections = useMemo(() => {
    const badgeCounts: Record<string, number | undefined> = {
      '/insights': navCounts?.insights,
      '/signals': navCounts?.signals,
      '/alerts': navCounts?.alerts,
      '/approvals': navCounts?.approvals,
      '/todos': navCounts?.todos,
    };
    const decorate = (rows: NavRow[]): NavRow[] =>
      rows
        .filter((row) => !row.adminOnly || isOrgAdmin)
        .map((row) => {
          const count = badgeCounts[row.to];
          if (count === undefined || count <= 0) return row;
          const displayValue = count > 99 ? '99+' : String(count);
          return { ...row, badge: { value: displayValue, tone: SURFACE_BADGE_TONES[row.to] } };
        });

    // Reorder Home rows per the personalization profile when it includes
    // any /today /briefs /briefing paths; rows not in the profile fall
    // to the end so we never hide a known nav row.
    const reorderHome = (rows: NavRow[]): NavRow[] => {
      if (!profile) return rows;
      const byTo = new Map(rows.map((r) => [r.to, r]));
      const ordered: NavRow[] = [];
      for (const to of profile.navOrder) {
        const row = byTo.get(to);
        if (row) {
          ordered.push(row);
          byTo.delete(to);
        }
      }
      return [...ordered, ...byTo.values()];
    };

    return SECTIONS.map((section) => ({
      ...section,
      rows: section.id === 'home' ? decorate(reorderHome(section.rows)) : decorate(section.rows),
    })).filter((section) => section.rows.length > 0);
  }, [navCounts, isOrgAdmin, profile]);

  const activeSectionData =
    visibleSections.find((s) => s.id === activeSection) ?? visibleSections[0];

  // Rail buttons render the top sections (home/operate/inbox/knowledge/
  // trust). The Settings section icon anchors the bottom of the rail
  // separately so it stays pinned regardless of section count. The
  // workspace switcher chip (`OrgSwitcherChip`) lives in the contextual
  // panel below — NOT in the icon rail — so it's hidden when the
  // sidebar collapses to rail-only. That's intentional: the chip
  // surfaces the workspace name + role label, which would be illegible
  // in a 56px-wide rail. Operators who need to switch workspaces while
  // collapsed hover-expand the panel via `.portal-side:hover`.
  const railTopSections = visibleSections.filter((s) => s.id !== 'settings');
  const railSettingsSection = visibleSections.find((s) => s.id === 'settings');

  const orgName = activeOrg?.name ?? '—';
  const orgSlug = user?.email?.split('@')[0] ?? '';
  const userInitials = initialsFor(user?.displayName ?? user?.email);
  const userName = user?.displayName?.trim() || user?.email || '';
  const userRole = activeOrg?.role ? (activeOrg.role === 'admin' ? 'Admin' : 'Member') : '';

  return (
    <nav
      className="portal-side-inner two-tier"
      aria-label={t('sidebar.nav.aria')}
      data-testid="sidebar-nav"
    >
      {/* Icon rail — section icons + workspace + settings pill. */}
      <div className="lp-rail-icons" data-testid="sidebar-rail">
        <div className="lp-rail-brand">
          <BrandMark size={22} showWordmark={false} className="text-[var(--color-ink)]" />
        </div>
        <div className="lp-rail-stack">
          {railTopSections.map((section) => (
            <RailButton key={section.id} section={section} active={section.id === activeSection} />
          ))}
        </div>
        <div className="lp-rail-stack mt-auto">
          <button
            type="button"
            onClick={toggleSidePin}
            aria-label={sidePinned ? t('sidebar.pin.unpin.aria') : t('sidebar.pin.pin.aria')}
            title={sidePinned ? t('sidebar.pin.unpin.title') : t('sidebar.pin.pin.title')}
            className="lp-rail-btn"
            data-testid="sidebar-pin-toggle"
          >
            <span aria-hidden className="text-xs">
              {sidePinned ? '◆' : '◇'}
            </span>
          </button>
          {railSettingsSection && (
            <RailButton section={railSettingsSection} active={activeSection === 'settings'} />
          )}
        </div>
      </div>

      {/* Contextual panel — only the active section's items. */}
      {activeSectionData && (
        <div className="lp-side-panel" data-testid="sidebar-panel">
          <div className="lp-side-panel-head">
            <span className="lp-side-panel-title side-label-text">{activeSectionData.label}</span>
          </div>
          <ul className="lp-side-list" data-testid="sidebar-list">
            {activeSectionData.rows.map((row) => (
              <NavLinkRow key={row.to} row={row} />
            ))}
          </ul>

          <div className="mt-auto flex flex-col gap-2">
            {/* Org+user chip — clickable org switcher when the signed-in
             *  user belongs to more than one workspace. */}
            <OrgSwitcherChip
              orgName={orgName}
              userRole={userRole}
              userInitials={userInitials}
              orgSlug={orgSlug}
              userName={userName}
              activeOrgId={activeOrg?.id ?? null}
              memberships={memberships}
              onSwitch={async (orgId) => {
                try {
                  await switchOrg(orgId);
                } catch (err) {
                  warnUnlessAbort('sidebar.switchOrg failed', err);
                  toast.error(t('sidebar.switchOrg.error'));
                }
              }}
            />

            <button
              type="button"
              onClick={() => openPalette(true)}
              className="rounded-[8px] border border-[var(--color-line)] p-3 text-start cursor-pointer transition-colors hover:border-[var(--color-accent)]"
              style={{
                background: 'linear-gradient(180deg, var(--color-surface-3), var(--color-surface))',
              }}
              aria-label={t('sidebar.agent.openActivity.aria')}
              data-testid="sidebar-agent-strap"
            >
              <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-accent)]">
                <span className="inline-block size-[6px] rounded-full bg-[var(--color-accent)]" />
                <span className="side-label-text">AGENT · WATCHING</span>
              </div>
              <div className="side-label-text mt-1.5 text-[12.5px] leading-[1.4] text-[var(--color-ink)]">
                {navCounts
                  ? tFormat('sidebar.agent.strap', {
                      sources: navCounts.sources,
                      signals: navCounts.signals,
                    })
                  : t('sidebar.agent.strap.loading')}
                <br />
                {t('sidebar.agent.tap-hint')}
              </div>
              <div className="side-label-text mt-2 flex justify-end font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--color-ink-muted)]">
                <span>⌘K</span>
              </div>
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

/**
 * RailButton — single icon-rail entry. Clicking navigates to the
 * section's first row (so the section's panel auto-renders on the next
 * route change). 44×44 touch target floor.
 */
function RailButton({ section, active }: { section: NavSection; active: boolean }) {
  const firstRow = section.rows[0];
  if (!firstRow) return null;
  return (
    <Link
      to={firstRow.to as never}
      className={cn('lp-rail-btn', active && 'active')}
      title={section.label}
      aria-label={section.label}
      data-testid={`sidebar-rail-${section.id}`}
    >
      <section.icon className="size-[18px]" strokeWidth={1.6} aria-hidden />
      <span className="lp-rail-tooltip" aria-hidden>
        {section.label}
      </span>
    </Link>
  );
}

function NavLinkRow({ row }: { row: NavRow }) {
  const matches = useRouterState({ select: (s) => s.location.pathname });
  const isActive =
    matches === row.to ||
    (row.to !== '/' && matches.startsWith(`${row.to}/`)) ||
    (row.to === '/today' && matches === '/');
  return (
    <li>
      <Link
        to={row.to as never}
        className={cn(
          'group relative flex items-center gap-2.5 rounded-[8px] px-2.5 py-1.5 text-[13.5px] text-[var(--color-ink-secondary)] transition-colors hover:bg-white/[0.03] hover:text-[var(--color-ink)]',
          isActive && 'bg-[var(--color-surface)] text-[var(--color-ink)]',
        )}
        title={row.label}
      >
        {isActive && (
          <span
            className="absolute start-[-12px] top-2 bottom-2 w-[2px] rounded-[2px] bg-[var(--color-accent)]"
            aria-hidden
          />
        )}
        <NavIcon icon={row.icon} isActive={isActive} />
        <span className="side-label-text">{row.label}</span>
        {row.badge && <NavBadge badge={row.badge} />}
      </Link>
    </li>
  );
}

function NavIcon({ icon: IconCmp, isActive }: { icon: LucideIcon; isActive: boolean }) {
  return (
    <IconCmp
      className={cn(
        'size-[15px] shrink-0',
        isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-ink-muted)]',
      )}
      strokeWidth={1.6}
      aria-hidden
    />
  );
}

function NavBadge({ badge }: { badge: NonNullable<NavRow['badge']> }) {
  const toneClass =
    badge.tone === 'amber'
      ? 'text-[var(--color-accent)] bg-[var(--color-accent-soft)]'
      : badge.tone === 'rose'
        ? 'text-[var(--color-rose)] bg-[var(--color-rose-soft)]'
        : 'text-[var(--color-ink-muted)] bg-[var(--color-surface-2)]';
  return (
    <span
      className={cn(
        'side-label-text ms-auto rounded-full px-1.5 py-px font-mono text-[10px]',
        toneClass,
      )}
    >
      {badge.value}
    </span>
  );
}

interface OrgSwitcherChipProps {
  orgName: string;
  userRole: string;
  userInitials: string;
  orgSlug: string;
  userName: string;
  activeOrgId: string | null;
  memberships: ReadonlyArray<OrgSummary>;
  onSwitch: (orgId: string) => Promise<void>;
}

function OrgSwitcherChip({
  orgName,
  userRole,
  userInitials,
  orgSlug,
  userName,
  activeOrgId,
  memberships,
  onSwitch,
}: OrgSwitcherChipProps) {
  const [switching, setSwitching] = useState<string | null>(null);
  const navigate = useNavigate();
  const hasChoice = memberships.length > 1;

  const triggerInner = (
    <>
      <span
        className="inline-flex size-[22px] items-center justify-center rounded-[5px] bg-[var(--color-accent)] font-mono text-[11px] font-semibold"
        style={{ color: 'var(--color-canvas)' }}
      >
        {orgName.slice(0, 1).toUpperCase()}
      </span>
      <span className="side-label-text flex-1 leading-[1.15]">
        <b className="block font-medium text-[var(--color-ink)]">{orgName}</b>
        <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--color-ink-muted)]">
          {userRole}
          {userInitials && ` · ${userInitials}`}
          {!userInitials && orgSlug && ` · ${orgSlug}`}
        </span>
      </span>
      <span
        className="side-label-text text-[14px] text-[var(--color-ink-faint)]"
        aria-hidden
        title={userName || ''}
      >
        ⌃
      </span>
    </>
  );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Active organization · ${orgName} · ${
            hasChoice ? 'switch' : t('onboarding.createOrg.switcher.create')
          }`}
          className="flex w-full items-center gap-2.5 rounded-[8px] border border-[var(--color-line)] bg-[var(--color-surface)] p-2 text-start text-[13px] hover:border-[var(--color-line-strong)] focus:outline-none focus-visible:border-[var(--color-accent)]"
          data-testid="sidebar-org-switcher"
        >
          {triggerInner}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="top"
          align="start"
          sideOffset={6}
          className="z-50 min-w-[220px] rounded-[8px] border border-[var(--color-line-strong)] bg-[var(--color-surface)] p-1 shadow-lg [box-shadow:0_12px_32px_-12px_color-mix(in_oklab,var(--color-canvas-deep)_60%,transparent)]"
        >
          {hasChoice && (
            <DropdownMenu.Label className="px-2 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-ink-muted)]">
              Switch organization
            </DropdownMenu.Label>
          )}
          {hasChoice &&
            memberships.map((m) => {
              const isActive = m.id === activeOrgId;
              return (
                <DropdownMenu.Item
                  key={m.id}
                  disabled={switching !== null}
                  onSelect={(e) => {
                    if (isActive) return;
                    e.preventDefault();
                    setSwitching(m.id);
                    void onSwitch(m.id).finally(() => setSwitching(null));
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-[6px] px-2 py-1.5 text-[13px] text-[var(--color-ink-secondary)] outline-none',
                    'data-[highlighted]:bg-[var(--color-surface-2)] data-[highlighted]:text-[var(--color-ink)]',
                    isActive && 'text-[var(--color-ink)]',
                    switching !== null && switching !== m.id && 'opacity-60',
                  )}
                >
                  <span
                    className="inline-flex size-[20px] items-center justify-center rounded-[4px] bg-[var(--color-accent)] font-mono text-[10px] font-semibold"
                    style={{ color: 'var(--color-canvas)' }}
                  >
                    {m.name.slice(0, 1).toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    <span className="block truncate font-medium">{m.name}</span>
                    <span className="block font-mono text-[10px] uppercase tracking-[0.04em] text-[var(--color-ink-muted)]">
                      {m.role}
                    </span>
                  </span>
                  {isActive ? (
                    <Check className="size-3.5 shrink-0 text-[var(--color-accent)]" aria-hidden />
                  ) : switching === m.id ? (
                    <span className="font-mono text-[10px] text-[var(--color-ink-muted)]">…</span>
                  ) : null}
                </DropdownMenu.Item>
              );
            })}
          <DropdownMenu.Item
            onSelect={(e) => {
              e.preventDefault();
              void navigate({ to: '/onboarding/create-org' });
            }}
            className="flex cursor-pointer items-center gap-2 rounded-[6px] px-2 py-1.5 text-[12.5px] text-[var(--color-accent)] outline-none data-[highlighted]:bg-[var(--color-surface-2)]"
          >
            <Plus className="size-3.5" aria-hidden />
            <span>{t('onboarding.createOrg.switcher.create')}</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
